import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { useEffect, useState, type CSSProperties } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  EyeOff,
  ExternalLink,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { sectionsApi, tilesApi, reorderApi, departmentsApi } from "../api";
import type { Section, Tile } from "../types";
import SectionForm from "./components/SectionForm";
import TileForm from "./components/TileForm";

export default function DepartmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const dept = useQuery({
    queryKey: ["admin-department", id],
    queryFn: () =>
      departmentsApi.list().then((r) => r.departments.find((d) => d.id === id)),
    enabled: !!id,
  });

  const sectionsQ = useQuery({
    queryKey: ["admin-sections", id],
    queryFn: () => sectionsApi.list(id),
    enabled: !!id,
  });

  const tilesQ = useQuery({
    queryKey: ["admin-tiles-for-dept", id],
    queryFn: async () => {
      const sections = (await sectionsApi.list(id)).sections;
      const all: Tile[] = [];
      for (const s of sections) {
        const r = await tilesApi.list(s.id);
        all.push(...r.tiles);
      }
      return all;
    },
    enabled: !!id,
  });

  const [sections, setSections] = useState<Section[]>([]);
  const [tilesBySection, setTilesBySection] = useState<Record<string, Tile[]>>({});

  useEffect(() => {
    if (sectionsQ.data) setSections(sectionsQ.data.sections);
  }, [sectionsQ.data]);

  useEffect(() => {
    if (tilesQ.data) {
      const map: Record<string, Tile[]> = {};
      for (const t of tilesQ.data) {
        (map[t.section_id ?? ""] ??= []).push(t);
      }
      for (const k of Object.keys(map)) map[k].sort((a, b) => a.order - b.order);
      setTilesBySection(map);
    }
  }, [tilesQ.data]);

  const [creatingSection, setCreatingSection] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [tileForm, setTileForm] = useState<{ sectionId: string; tile: Tile | null } | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const reorderSections = useMutation({
    mutationFn: (ids: string[]) =>
      reorderApi.apply({ type: "section", parent_id: id!, ordered_ids: ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content"] });
      qc.invalidateQueries({ queryKey: ["admin-sections", id] });
    },
  });

  const reorderTiles = useMutation({
    mutationFn: ({ sectionId, ids }: { sectionId: string; ids: string[] }) =>
      reorderApi.apply({ type: "tile", parent_id: sectionId, ordered_ids: ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content"] });
      qc.invalidateQueries({ queryKey: ["admin-tiles-for-dept", id] });
    },
  });

  const deleteSection = useMutation({
    mutationFn: (sid: string) => sectionsApi.remove(sid),
    onSuccess: () => {
      toast.success("Section deleted");
      qc.invalidateQueries({ queryKey: ["content"] });
      qc.invalidateQueries({ queryKey: ["admin-sections", id] });
      qc.invalidateQueries({ queryKey: ["admin-tiles-for-dept", id] });
    },
  });

  const deleteTile = useMutation({
    mutationFn: (tid: string) => tilesApi.remove(tid),
    onSuccess: () => {
      toast.success("Tile deleted");
      qc.invalidateQueries({ queryKey: ["content"] });
      qc.invalidateQueries({ queryKey: ["admin-tiles-for-dept", id] });
    },
  });

  function onSectionDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex((s) => s.id === active.id);
    const newIdx = sections.findIndex((s) => s.id === over.id);
    const next = arrayMove(sections, oldIdx, newIdx);
    setSections(next);
    reorderSections.mutate(next.map((s) => s.id));
  }

  function onTileDragEnd(sectionId: string) {
    return (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const list = tilesBySection[sectionId] ?? [];
      const oldIdx = list.findIndex((t) => t.id === active.id);
      const newIdx = list.findIndex((t) => t.id === over.id);
      const next = arrayMove(list, oldIdx, newIdx);
      setTilesBySection({ ...tilesBySection, [sectionId]: next });
      reorderTiles.mutate({ sectionId, ids: next.map((t) => t.id) });
    };
  }

  if (!id) return null;
  if (dept.isLoading) return <div className="p-10 text-ink-500 text-[13px]">Loading…</div>;
  if (!dept.data)
    return <div className="p-10 text-ink-500 text-[13px]">Department not found.</div>;

  const accentStyle: CSSProperties = { ["--accent-color" as never]: dept.data.color };

  return (
    <div className="max-w-5xl mx-auto px-10 py-10">
      <Link
        to="/admin/departments"
        className="inline-flex items-center gap-1 text-[12.5px] text-ink-500 hover:text-ink-900 mb-5 transition-colors"
      >
        <ChevronLeft className="size-3.5" /> All departments
      </Link>

      <header className="mb-9 flex items-start justify-between gap-6 pb-7 border-b border-ink-200/70">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="dot" style={{ backgroundColor: dept.data.color }} aria-hidden />
            <span className="eyebrow font-mono">/{dept.data.slug}</span>
          </div>
          <h1 className="display text-[36px] font-medium tracking-tight">
            <span className="italic">{dept.data.name}</span>
          </h1>
          {dept.data.description && (
            <p className="text-[13.5px] text-ink-500 mt-1.5 max-w-2xl">{dept.data.description}</p>
          )}
        </div>
        <button onClick={() => setCreatingSection(true)} className="btn-primary shrink-0">
          <Plus className="size-3.5" /> New section
        </button>
      </header>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSectionDragEnd}>
        <SortableContext
          items={sections.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-6">
            {sections.map((s) => (
              <SectionBlock
                key={s.id}
                section={s}
                tiles={tilesBySection[s.id] ?? []}
                accentStyle={accentStyle}
                sensors={sensors}
                onTileDragEnd={onTileDragEnd(s.id)}
                onAddTile={() => setTileForm({ sectionId: s.id, tile: null })}
                onEditTile={(t) => setTileForm({ sectionId: s.id, tile: t })}
                onDeleteTile={(t) => {
                  if (confirm(`Delete tile "${t.name}"?`)) deleteTile.mutate(t.id);
                }}
                onEditSection={() => setEditingSection(s)}
                onDeleteSection={() => {
                  if (confirm(`Delete section "${s.name}" and all its tiles?`)) {
                    deleteSection.mutate(s.id);
                  }
                }}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {sections.length === 0 && (
        <div className="surface px-8 py-12 text-center text-[13px] text-ink-500">
          No sections yet. Add the first one to start organising tiles.
        </div>
      )}

      {(creatingSection || editingSection) && (
        <SectionForm
          departmentId={id}
          existing={editingSection}
          onClose={() => {
            setCreatingSection(false);
            setEditingSection(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin-sections", id] });
            qc.invalidateQueries({ queryKey: ["content"] });
          }}
        />
      )}

      {tileForm && (
        <TileForm
          sectionId={tileForm.sectionId}
          existing={tileForm.tile}
          onClose={() => setTileForm(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin-tiles-for-dept", id] });
            qc.invalidateQueries({ queryKey: ["content"] });
          }}
        />
      )}
    </div>
  );
}

function SectionBlock({
  section,
  tiles,
  accentStyle,
  sensors,
  onTileDragEnd,
  onAddTile,
  onEditTile,
  onDeleteTile,
  onEditSection,
  onDeleteSection,
}: {
  section: Section;
  tiles: Tile[];
  accentStyle: CSSProperties;
  sensors: ReturnType<typeof useSensors>;
  onTileDragEnd: (e: DragEndEvent) => void;
  onAddTile: () => void;
  onEditTile: (t: Tile) => void;
  onDeleteTile: (t: Tile) => void;
  onEditSection: () => void;
  onDeleteSection: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        ...accentStyle,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="surface accent-top overflow-hidden"
    >
      <header className="px-4 py-2.5 border-b border-ink-100 flex items-center gap-2.5 bg-cream-50">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab text-ink-300 hover:text-ink-700 transition-colors"
          aria-label="drag section"
        >
          <GripVertical className="size-3.5" />
        </button>
        <h3 className="display text-[15px] font-medium flex-1 text-ink-900 tracking-tight">
          {section.name}
        </h3>
        <span className="text-[11px] text-ink-400 font-mono">{tiles.length}</span>
        <button onClick={onAddTile} className="btn-secondary btn-sm">
          <Plus className="size-3" /> Service
        </button>
        <button onClick={onEditSection} className="btn-icon" aria-label="edit section">
          <Pencil className="size-3.5" />
        </button>
        <button
          onClick={onDeleteSection}
          className="btn-icon hover:!bg-accent-50 hover:!text-accent-600"
          aria-label="delete section"
        >
          <Trash2 className="size-3.5" />
        </button>
      </header>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onTileDragEnd}>
        <SortableContext items={tiles.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          <ul className="divide-y divide-ink-100">
            {tiles.map((t) => (
              <TileRow
                key={t.id}
                tile={t}
                onEdit={() => onEditTile(t)}
                onDelete={() => onDeleteTile(t)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {tiles.length === 0 && (
        <div className="px-4 py-6 text-center text-[12px] text-ink-400">No services yet.</div>
      )}
    </div>
  );
}

function TileRow({
  tile,
  onEdit,
  onDelete,
}: {
  tile: Tile;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tile.id,
  });
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="px-3.5 py-2.5 flex items-center gap-2.5 hover:bg-cream-50 group"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-ink-300 hover:text-ink-700 transition-colors"
        aria-label="drag tile"
      >
        <GripVertical className="size-3.5" />
      </button>
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-[13px] font-medium text-ink-900 truncate">{tile.name}</span>
        {tile.badge && <span className="chip-accent">{tile.badge}</span>}
        {tile.hidden && (
          <span className="chip">
            <EyeOff className="size-3" /> Hidden
          </span>
        )}
      </div>
      {tile.kind === "content" ? (
        <span className="chip">
          <FileText className="size-3" /> Content
        </span>
      ) : (
        <a
          href={tile.url ?? "#"}
          target="_blank"
          rel="noreferrer"
          className="text-[11.5px] text-ink-400 hover:text-ink-700 truncate max-w-[280px] inline-flex items-center gap-1 font-mono"
          onClick={(e) => e.stopPropagation()}
        >
          {(tile.url ?? "").replace(/^https?:\/\//, "")}
          <ExternalLink className="size-3 shrink-0" />
        </a>
      )}
      <button onClick={onEdit} className="btn-icon" aria-label="edit">
        <Pencil className="size-3.5" />
      </button>
      <button
        onClick={onDelete}
        className="btn-icon hover:!bg-accent-50 hover:!text-accent-600"
        aria-label="delete"
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  );
}
